import React from 'react';
import {
  ChevronDown,
  ChevronRight,
  Copy,
  Maximize2,
  Minimize2,
  Rows3,
} from 'lucide-react';

import { Button } from '@/Modules/Core/Components/shadcn/button';
import { cn } from '@/lib/utils';

const parsePayloadValue = (payloadText) => {
  if (!payloadText) {
    return null;
  }

  if (typeof payloadText === 'object') {
    return payloadText;
  }

  if (typeof payloadText !== 'string') {
    return null;
  }

  try {
    return JSON.parse(payloadText);
  } catch (_error) {
    return null;
  }
};

const isComplexValue = (value) =>
  Boolean(value) && typeof value === 'object';

const getPreviewLabel = (value) => {
  if (Array.isArray(value)) {
    return `${value.length} items`;
  }

  if (isComplexValue(value)) {
    return `${Object.keys(value).length} items`;
  }

  return '';
};

const formatPrimitive = (value) => {
  if (typeof value === 'string') {
    return `"${value}"`;
  }

  if (value === null) {
    return 'null';
  }

  return String(value);
};

const getPrimitiveClassName = (value) => {
  if (typeof value === 'string') {
    return 'text-emerald-700 dark:text-emerald-300';
  }

  if (typeof value === 'number') {
    return 'text-amber-700 dark:text-amber-300';
  }

  if (typeof value === 'boolean') {
    return 'text-violet-700 dark:text-violet-300';
  }

  if (value === null) {
    return 'italic text-muted-foreground';
  }

  return 'text-foreground';
};

const JsonNode = ({
  name,
  value,
  depth,
  path,
  viewMode,
  manualStates,
  onToggle,
  isRoot = false,
}) => {
  const complex = isComplexValue(value);
  const isArray = Array.isArray(value);
  const entries = complex
    ? isArray
      ? value.map((item, index) => [index, item])
      : Object.entries(value)
    : [];

  const autoCollapsed =
    viewMode === 'collapsed'
      ? depth >= 0
      : viewMode === 'preview'
        ? depth >= 1
        : false;
  const isCollapsed = complex
    ? manualStates[path] ?? autoCollapsed
    : false;

  const label = isArray ? '[' : '{';
  const closingLabel = isArray ? ']' : '}';

  return (
    <div className={cn(!isRoot && 'ml-4 border-l border-border/35 pl-4')}>
      <div
        className={cn(
          'flex flex-wrap items-start gap-1 font-mono text-[13px] leading-7',
          depth === 0 && 'text-[14px]'
        )}
      >
        {complex ? (
          <button
            type="button"
            className="mt-1 inline-flex h-4 w-4 appearance-none items-center justify-center rounded-sm border-0 bg-transparent p-0 text-muted-foreground shadow-none transition-colors hover:bg-muted hover:text-foreground"
            onClick={() => onToggle(path, isCollapsed)}
            aria-label={isCollapsed ? 'Expand JSON item' : 'Collapse JSON item'}
          >
            {isCollapsed ? (
              <ChevronRight className="h-3.5 w-3.5 shrink-0" />
            ) : (
              <ChevronDown className="h-3.5 w-3.5 shrink-0" />
            )}
          </button>
        ) : (
          <span className="inline-block h-4 w-4" />
        )}

        {name !== null ? (
          <>
            <span className="text-sky-700 dark:text-sky-300">"{name}"</span>
            <span className="text-muted-foreground">:</span>
          </>
        ) : null}

        {complex ? (
          <>
            <span className="text-foreground">{label}</span>
            <span className="text-muted-foreground">{getPreviewLabel(value)}</span>
            {isCollapsed ? (
              <span className="text-muted-foreground">{closingLabel}</span>
            ) : null}
          </>
        ) : (
          <span className={getPrimitiveClassName(value)}>
            {formatPrimitive(value)}
          </span>
        )}
      </div>

      {complex && !isCollapsed ? (
        <div className="space-y-1">
          {entries.map(([entryKey, entryValue]) => (
            <JsonNode
              key={`${path}.${String(entryKey)}`}
              name={String(entryKey)}
              value={entryValue}
              depth={depth + 1}
              path={`${path}.${String(entryKey)}`}
              viewMode={viewMode}
              manualStates={manualStates}
              onToggle={onToggle}
            />
          ))}

          <div className="font-mono text-[13px] leading-7 text-foreground">
            {closingLabel}
          </div>
        </div>
      ) : null}
    </div>
  );
};

const WebHookJsonViewer = ({ payloadText, onCopy }) => {
  const [viewMode, setViewMode] = React.useState('preview');
  const [manualStates, setManualStates] = React.useState({});
  const parsedValue = React.useMemo(() => parsePayloadValue(payloadText), [payloadText]);

  React.useEffect(() => {
    setViewMode('preview');
    setManualStates({});
  }, [payloadText]);

  const handleSetViewMode = React.useCallback((nextMode) => {
    setViewMode(nextMode);
    setManualStates({});
  }, []);

  const handleToggle = React.useCallback((path, currentValue) => {
    setManualStates((previous) => ({
      ...previous,
      [path]: !currentValue,
    }));
  }, []);

  return (
    <div className="rounded-2xl border border-border/25 bg-background/80 p-4 dark:bg-card/50">
      <div className="mb-4 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <div>
          <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">
            Payload
          </div>
          <div className="mt-1 text-[12px] text-muted-foreground">
            Expand, collapse, or minimize the JSON structure for easier inspection.
          </div>
        </div>

        <div className="flex flex-wrap gap-2">
          <Button
            type="button"
            variant="outline"
            className="border-border/35 bg-background/80 hover:bg-muted/60 dark:bg-card/50 dark:hover:bg-background/60"
            onClick={() => handleSetViewMode('expanded')}
          >
            <Maximize2 className="mr-2 h-4 w-4" />
            Expand all
          </Button>

          <Button
            type="button"
            variant="outline"
            className="border-border/35 bg-background/80 hover:bg-muted/60 dark:bg-card/50 dark:hover:bg-background/60"
            onClick={() => handleSetViewMode('preview')}
          >
            <Rows3 className="mr-2 h-4 w-4" />
            Minimize
          </Button>

          <Button
            type="button"
            variant="outline"
            className="border-border/35 bg-background/80 hover:bg-muted/60 dark:bg-card/50 dark:hover:bg-background/60"
            onClick={() => handleSetViewMode('collapsed')}
          >
            <Minimize2 className="mr-2 h-4 w-4" />
            Collapse all
          </Button>

          <Button
            type="button"
            variant="outline"
            className="border-border/35 bg-background/80 hover:bg-muted/60 dark:bg-card/50 dark:hover:bg-background/60"
            onClick={onCopy}
          >
            <Copy className="mr-2 h-4 w-4" />
            Copy JSON
          </Button>
        </div>
      </div>

      {parsedValue ? (
        <div className="themed-slim-scrollbar max-h-[70vh] overflow-auto rounded-xl border border-border/25 bg-muted/30 p-4 dark:bg-background/70">
          <JsonNode
            name={null}
            value={parsedValue}
            depth={0}
            path="root"
            viewMode={viewMode}
            manualStates={manualStates}
            onToggle={handleToggle}
            isRoot
          />
        </div>
      ) : (
        <pre className="themed-slim-scrollbar max-h-[70vh] overflow-auto rounded-xl border border-border/25 bg-muted/30 p-4 font-mono text-[12px] leading-6 text-muted-foreground dark:bg-background/70">
          {payloadText || 'Payload unavailable.'}
        </pre>
      )}
    </div>
  );
};

export default WebHookJsonViewer;
